import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { forkJoin, Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import { User } from '../../shared/models/user.model';
import { LanguageEnum } from '../../shared/enums/language.enum';
import { DietaryRequirement } from '../../shared/enums/dietaryRequirementEnum';
import { UnitSystem } from '../../shared/enums/unitSystem.enum';
import { UserRole } from '../../shared/enums/userRole.enum';

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private USER_URL = `${environment.apiUrl}/users`;

  constructor(private http: HttpClient) {}

  getUserCount(): Observable<{ count: number }> {
    return this.http.get<{ count: number }>(`${this.USER_URL}/count`);
  }

  getAllUsers(): Observable<User[]> {
    return this.http.get<User[]>(`${this.USER_URL}/all`);
  }

  getUserById(userId: number): Observable<User> {
    return this.http.get<User>(`${this.USER_URL}/${userId}`);
  }

  // Only for name update now (email and password are separate)
  updateProfile(data: { name: string }): Observable<User> {
    return this.http.patch<User>(`${this.USER_URL}/me`, data);
  }

  updateEmail(newEmail: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.USER_URL}/me/email`, { email: newEmail });
  }

  updatePassword(data: { currentPassword: string; newPassword: string }): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.USER_URL}/me/password`, data);
  }

  deleteAccount(): Observable<void> {
    return this.http.delete<void>(`${this.USER_URL}/me`);
  }

  updatePreferences(preferences: {
    preferred_language: LanguageEnum;
    preferred_dietary: DietaryRequirement[];
    preferred_unit_system: UnitSystem;
    preferred_timezone?: string;
  }): Observable<void> {
    return this.http.patch<void>(`${this.USER_URL}/me/preferences`, preferences);
  }

  updateUserRole(username: string, role: UserRole): Observable<{ message: string }> {
    return this.http.put<{ message: string }>(`${this.USER_URL}/${username}/role`, { role });
  }

  updateOnboardingStatus(isComplete: boolean): Observable<void> {
    return this.http.patch<void>(`${this.USER_URL}/me/onboarding-status`, { onboarding_complete: isComplete });
  }

  completeOnboarding(data: {
    preferred_language: LanguageEnum;
    preferred_dietary: DietaryRequirement[];
    preferred_unit_system: UnitSystem;
    onboarding_complete: boolean;
  }): Observable<void[]> {
    return forkJoin([
      this.updatePreferences(data),
      this.updateOnboardingStatus(data.onboarding_complete),
    ]);
  }

  updateMarketplaceSubscription(subscribed: boolean): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.USER_URL}/me/marketplace-subscription`, { subscribed });
  }

  getLinkedAccounts(): Observable<{ provider: string; created_at: string }[]> {
    return this.http.get<{ provider: string; created_at: string }[]>(`${this.USER_URL}/me/linked-accounts`);
  }

}
